import {Flex, Paper, Text} from '@mantine/core';
import type {FC, ReactNode} from 'react';
import {DeleteButton} from 'src/design/molecules/DeleteButton';

/**
 * Bordered card with a numbered header (index + 1) and a delete action,
 * wrapping a responsive row of children plus optional description text.
 * @registry Numbered, removable card with a responsive content row.
 *
 * Ported from `library-m/forms/ConditionCard`. Renamed `ConditionCard` to
 * `RemovableCard` to be domain-neutral; prop surface otherwise identical.
 */
export interface RemovableCardProps {
  index: number;
  onDelete: () => void;
  disabled?: boolean;
  description?: string;
  children: ReactNode;
}

export const RemovableCard: FC<RemovableCardProps> = ({
  index,
  onDelete,
  disabled,
  description,
  children,
}) => {
  return (
    <Paper withBorder shadow="xs" p="md">
      <Flex justify="space-between" align="center" mb="sm">
        <Text size="sm" fw={600}>
          {index + 1}
        </Text>
        <DeleteButton onClick={onDelete} size="xs" disabled={disabled} />
      </Flex>
      <Flex
        direction={{base: 'column', lg: 'row'}}
        gap="md"
        align={{base: 'stretch', lg: 'flex-end'}}>
        {children}
      </Flex>
      {description && (
        <Text size="sm" c="dimmed" mt="sm">
          {description}
        </Text>
      )}
    </Paper>
  );
};
